import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { APIURL } from '../../../config';
import { CircularProgress, Card } from '@mui/material';
import PatientDetailPanel from './PatientDetailPanel';
import MedicationSection from './MedicationSection';
import IncidentSection from './IncidentSection';
import AssessmentSection from './AssessmentSection';
import VaccineSection from './VaccineSection';
import MedicareSection from './MedicareSection';

function Health({ cust_details, user }) {
    const [adlData, setAdlData] = useState(null);
    const [medications, setMedications] = useState([]);
    const [incidents, setIncidents] = useState([]);
    const [assessments, setAssessments] = useState([]);
    const [vaccines, setVaccines] = useState([]);
    const [extensionData, setExtensionData] = useState(null);
    const [loading, setLoading] = useState(true);

    const customer_id = cust_details?.cus_id;
    const addedBy = user?.emp_id;

    const fetchAll = useCallback(async () => {
        if (!customer_id) return;
        setLoading(true);
        try {
            const [adlRes, medRes, incRes, assessRes, vacRes, extRes] = await Promise.allSettled([
                axios.get(APIURL + `/customer_adl/${customer_id}`),
                axios.get(APIURL + `/customer_medication/by_customer/${customer_id}`),
                axios.get(APIURL + `/customer_incident/by_customer/${customer_id}`),
                axios.get(APIURL + `/customer_assessment/by_customer/${customer_id}`),
                axios.get(APIURL + `/customer_vaccine/by_customer/${customer_id}`),
                axios.get(APIURL + `/customer_extension`, { params: { cus_id: customer_id } }),
            ]);
            setAdlData(adlRes.value.data?.[0] ? adlRes.value.data[0] : null);
            setMedications(medRes.value.data ?? []);
            setIncidents(incRes.value.data ?? []);
            setAssessments(assessRes.value.data ?? []);
            setVaccines(vacRes.value?.data ?? []);
            setExtensionData(extRes.value.data?.[0] ? extRes.value.data[0] : null);
        } catch (err) {
            console.error(err);
        }
        setLoading(false);
    }, [customer_id]);

    useEffect(() => {
        fetchAll();
    }, [fetchAll]);

    if (loading) {
        return (
            <div className="d-flex justify-content-center" style={{ marginTop: 40 }}>
                <CircularProgress />
            </div>
        );
    }

    return (
        <Card variant="outlined" style={{ borderRadius: '10px', borderColor: '#85C4E5', background: '#F9F9F9', height: 'calc(100vh - 260px)', display: 'flex', padding: 12, marginBottom: 4, overflow: 'hidden' }}>
            <div style={{ display: 'flex', width: '100%', gap: 16 }}>
                <div style={{ display: 'flex', flexDirection: 'column', flex: 1, gap: 16, minHeight: 0 }}>
                    <div style={{ flex: 1, minHeight: 0 }}>
                        <PatientDetailPanel
                            cust_details={cust_details}
                            extensionData={extensionData}
                            adlData={adlData}
                            customer_id={customer_id}
                            addedBy={addedBy}
                            onADLSave={setAdlData}
                        />
                    </div>
                    <div style={{ flex: 1, minHeight: 0 }}>
                        <MedicareSection
                            custEmail={cust_details?.cus_email}
                            userEmail={user?.sec_email}
                        />
                    </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                    <MedicationSection
                        medications={medications}
                        customer_id={customer_id}
                        addedBy={addedBy}
                        onAdd={med => setMedications(prev => [med, ...prev])}
                        onDelete={id => setMedications(prev => prev.filter(m => m.customer_medication_id !== id))}
                    />
                    <VaccineSection
                        vaccines={vaccines}
                        customer_id={customer_id}
                        addedBy={addedBy}
                        onAdd={vaccine => setVaccines(prev => [vaccine, ...prev])}
                        onDelete={id => setVaccines(prev => prev.filter(v => v.customer_vaccine_id !== id))}
                    />
                    <IncidentSection
                        incidents={incidents}
                        customer_id={customer_id}
                        addedBy={addedBy}
                        onAdd={incident => setIncidents(prev => [incident, ...prev])}
                        onDelete={id => setIncidents(prev => prev.filter(i => i.customer_incident_id !== id))}
                    />
                    <AssessmentSection
                        assessments={assessments}
                        customer_id={customer_id}
                        addedBy={addedBy}
                        onAdd={assessment => setAssessments(prev => [assessment, ...prev])}
                        onDelete={id => setAssessments(prev => prev.filter(a => a.customer_assessment_id !== id))}
                    />
                </div>
            </div>
        </Card>
    );
}

export default Health;
